/**
 * useRefreshActions Hook
 * Phase 1 Day 6 - Device refresh and action handlers
 *
 * Manages device-related UI actions like refresh, device actions (lock/unlock),
 * and settings download. Coordinates between device operations, loading states,
 * and notifications to provide a complete action handling system.
 *
 * @param {Object} params - Hook parameters
 * @param {React.RefObject} params.selectedDeviceIdRef - Ref to currently selected device ID
 * @param {Function} params.authenticatedFetch - Authenticated fetch wrapper
 * @param {string} params.installationId - Current installation ID
 * @param {Array} params.devices - Array of devices
 * @param {Object} params.deviceStates - Map of device states
 * @param {Function} params.connectWebSocketStore - WebSocket connection function
 * @returns {Object} Refresh and action handlers
 */

import { useCallback, useRef } from "react";
import config from "../config";
import logger from "../utils/logger";
import { useLoadingStates } from "./useLoadingStates";
import { useNotification } from "./useNotification";
import { useDeviceOperations } from "./useDeviceOperations";
import { useModalManagement } from "./useModalManagement";
import { useDevicesStore } from "../stores";
import { useDeviceActions } from "./useDeviceActions";
import {
  collectDeviceSettings,
  downloadSettingsFile,
} from "../utils/deviceSettings";

const COMMON_HEADERS = {
  "Content-Type": "application/json",
  "x-api-key": config.apiKey,
};

export const useRefreshActions = ({
  selectedDeviceIdRef,
  authenticatedFetch,
  installationId,
  devices,
  deviceStates,
  connectWebSocketStore,
  refreshDevicesFromStore, // Function from DevicesStore
}) => {
  // Get loading state management
  const {
    isRefreshing,
    startRefreshing,
    stopRefreshing,
    setLoadingAction,
    clearLoading,
    startDownloadingSettings,
    stopDownloadingSettings,
  } = useLoadingStates();

  // Get notification functions
  const { showNotification } = useNotification();

  // Get device operations (cache management functions)
  const {
    invalidateInstallationSyncCache,
    setInstallationSyncCache,
    invalidateDeviceStateCache,
  } = useDeviceOperations({
    authenticatedFetch,
    installationId,
  });

  // Get device action executor
  const { executeDeviceAction, updateFirmware } = useDeviceActions(
    authenticatedFetch,
    invalidateDeviceStateCache
  );

  // Get modal management
  const { openPasswordModal, closePasswordModal } = useModalManagement();

  // Track password modal purpose: "download", "firmware", or "wifi"
  const passwordModalPurposeRef = useRef("download");

  // Pending WiFi setting change — stored until password is confirmed
  const pendingWifiActionRef = useRef(null);

  /**
   * Manual refresh - triggers full background sync
   */
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    logger.log("Refresh clicked. Ensuring WebSocket is connected...");
    connectWebSocketStore(config.websocketUrl);
    logger.log("Triggering full background sync...");
    startRefreshing();
    showNotification({
      message: "Full data sync initiated...",
      type: "success",
    });

    try {
      // Force sync and invalidate cache (manual refresh should always sync)
      invalidateInstallationSyncCache(installationId);

      const response = await authenticatedFetch(
        `${config.apiEndpoint}/refresh-data`,
        {
          method: "POST",
          headers: COMMON_HEADERS,
        }
      );

      if (!response.ok) {
        throw new Error(`Refresh request failed: ${response.status}`);
      }

      // Update cache after successful sync
      setInstallationSyncCache(installationId);

      // Re-fetch devices and device states after backend sync completes
      logger.log("Refresh completed, reloading devices and states...");
      logger.log(
        `[handleRefresh] Current selected device before refresh: ${selectedDeviceIdRef.current}`
      );
      logger.log(
        `[handleRefresh] React state - Device states: ${
          Object.keys(deviceStates).length
        } states`
      );
      logger.log(
        `[handleRefresh] React state - Devices: ${devices.length} devices`
      );

      // Log zustand store state directly
      const storeState = useDevicesStore.getState();
      logger.log(
        `[handleRefresh] Zustand store - Device states: ${
          Object.keys(storeState.deviceStates).length
        } states`
      );
      logger.log(
        `[handleRefresh] Zustand store - Devices: ${storeState.devices.length} devices`
      );
      logger.log(
        `[handleRefresh] Zustand store - Selected device: ${storeState.selectedDeviceId}`
      );

      try {
        logger.log(`[handleRefresh] Calling refreshDevicesFromStore...`);
        await refreshDevicesFromStore(installationId, authenticatedFetch);
        logger.log(
          `[handleRefresh] refreshDevicesFromStore completed successfully`
        );

        // Log store state after refresh
        const storeStateAfter = useDevicesStore.getState();
        logger.log(
          `[handleRefresh] After refresh - Zustand store has ${storeStateAfter.devices.length} devices`
        );
        logger.log(
          `[handleRefresh] After refresh - Zustand store has ${
            Object.keys(storeStateAfter.deviceStates).length
          } device states`
        );
      } catch (refreshError) {
        logger.error(
          `[handleRefresh] refreshDevicesFromStore failed:`,
          refreshError
        );
        throw refreshError;
      }

      logger.log(
        `[handleRefresh] React state after refresh - Selected device: ${selectedDeviceIdRef.current}`
      );
      logger.log(
        `[handleRefresh] React state after refresh - Device states: ${
          Object.keys(deviceStates).length
        } states`
      );
      logger.log(
        `[handleRefresh] React state after refresh - Devices: ${devices.length} devices`
      );

      showNotification({
        message: "Data refreshed successfully",
        type: "success",
      });
    } catch (error) {
      logger.error("Failed to trigger refresh:", error);
      showNotification({
        message: `Refresh failed: ${error.message}`,
        type: "error",
      });
    } finally {
      setTimeout(() => stopRefreshing(), 1500);
    }
  }, [
    isRefreshing,
    showNotification,
    connectWebSocketStore,
    installationId,
    refreshDevicesFromStore,
    invalidateInstallationSyncCache,
    setInstallationSyncCache,
    startRefreshing,
    stopRefreshing,
    deviceStates,
    devices.length,
    selectedDeviceIdRef,
    authenticatedFetch,
  ]);

  /**
   * Execute a device action (lock, unlock, etc.)
   */
  const handleDeviceAction = useCallback(
    async (actionType) => {
      const deviceId = selectedDeviceIdRef.current;
      if (!deviceId) {
        logger.error("No device selected.");
        return;
      }

      setLoadingAction(actionType);

      try {
        await executeDeviceAction(deviceId, actionType);
        showNotification({
          message: `Action '${actionType}' initiated successfully for device.`,
          type: "success",
        });
      } catch (error) {
        showNotification({
          message: `Action failed: ${error.message}`,
          type: "error",
        });
      } finally {
        clearLoading();
      }
    },
    [
      executeDeviceAction,
      showNotification,
      setLoadingAction,
      clearLoading,
      selectedDeviceIdRef,
    ]
  );

  /**
   * Initiate device settings download (opens password modal)
   */
  const handleDownloadSettings = useCallback(async () => {
    passwordModalPurposeRef.current = "download";
    openPasswordModal();
  }, [openPasswordModal]);

  /**
   * Initiate firmware update (opens password modal)
   */
  const handleUpdateFirmware = useCallback(async () => {
    passwordModalPurposeRef.current = "firmware";
    openPasswordModal();
  }, [openPasswordModal]);

  /**
   * Initiate a password-protected WiFi setting change (opens password modal)
   */
  const initiateWifiSettingChange = useCallback((setting, value, deviceId, updateCredentialsSetting, onSuccess) => {
    pendingWifiActionRef.current = { setting, value, deviceId, updateCredentialsSetting, onSuccess };
    passwordModalPurposeRef.current = "wifi";
    openPasswordModal();
  }, [openPasswordModal]);

  /**
   * Confirm password and execute the appropriate action based on modal purpose
   */
  const handlePasswordConfirm = useCallback(async () => {
    closePasswordModal();

    if (passwordModalPurposeRef.current === "wifi") {
      const pending = pendingWifiActionRef.current;
      pendingWifiActionRef.current = null;
      if (!pending) return;

      try {
        await pending.updateCredentialsSetting({
          deviceId: pending.deviceId,
          setting: pending.setting,
          value: pending.value,
        });
        showNotification({
          message: "Wi-Fi Enabled Status change submitted to device.",
          type: "info",
        });
        if (pending.onSuccess) pending.onSuccess();
      } catch (error) {
        logger.error("Error updating WiFi connectivity setting:", error);
        showNotification({
          message: "Failed to update Wi-Fi Enabled Status.",
          type: "error",
        });
      }
      return;
    }

    if (passwordModalPurposeRef.current === "firmware") {
      // Execute firmware update
      const deviceId = selectedDeviceIdRef.current;
      if (!deviceId) {
        showNotification({
          message: "No device selected.",
          type: "error",
        });
        return;
      }

      setLoadingAction("FIRMWARE_UPDATE");
      showNotification({
        message: "Initiating firmware update...",
        type: "info",
      });

      try {
        await updateFirmware(deviceId);
        showNotification({
          message: "Firmware update initiated successfully. The device will update shortly.",
          type: "success",
        });
      } catch (error) {
        logger.error("Error initiating firmware update:", error);
        showNotification({
          message: `Firmware update failed: ${error.message}`,
          type: "error",
        });
      } finally {
        clearLoading();
      }
      return;
    }

    // Default: download device settings
    const deviceId = selectedDeviceIdRef.current;
    if (!deviceId) {
      showNotification({
        message: "No device selected.",
        type: "error",
      });
      return;
    }

    const selectedDevice = useDevicesStore
      .getState()
      .devices.find((d) => d.deviceId === deviceId);
    if (!selectedDevice) {
      showNotification({
        message: "Selected device not found.",
        type: "error",
      });
      return;
    }

    startDownloadingSettings();
    showNotification({
      message: "Generating complete device settings...",
      type: "info",
    });

    try {
      const settings = await collectDeviceSettings(selectedDevice.deviceId);
      downloadSettingsFile(settings, selectedDevice.displayName);

      showNotification({
        message: "Device settings downloaded successfully",
        type: "success",
      });
    } catch (error) {
      logger.error("Error downloading device settings:", error);
      showNotification({
        message: `Failed to download settings: ${error.message}`,
        type: "error",
      });
    } finally {
      stopDownloadingSettings();
    }
  }, [
    closePasswordModal,
    selectedDeviceIdRef,
    showNotification,
    setLoadingAction,
    clearLoading,
    updateFirmware,
    startDownloadingSettings,
    stopDownloadingSettings,
  ]);

  return {
    // Actions
    handleRefresh,
    handleDeviceAction,
    handleDownloadSettings,
    handlePasswordConfirm,
    handleUpdateFirmware,
    initiateWifiSettingChange,

    // Password modal purpose
    passwordModalPurpose: passwordModalPurposeRef.current,

    // State (for backward compatibility)
    isRefreshing,
  };
};
